/**
 * Organizations Page - Business Logic Layer
 */

import { useState } from 'react'
import { useOrganizations } from '@/features/organizations/hooks/useOrganizations'
import { PageLayout } from '@/layouts/PageLayout'
import { OrganizationsTemplate } from '@/templates/OrganizationsTemplate'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Building2, Plus, Search, RefreshCw } from 'lucide-react'

export default function OrganizationsPage() {
  // ========================================
  // BUSINESS LOGIC
  // ========================================

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [createDialog, setCreateDialog] = useState(false)
  const limit = 10

  const { data, isLoading, error, refetch } = useOrganizations(page, limit, search)

  const handleRefresh = () => {
    refetch()
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const organizations = data?.organizations || []
  const total = data?.total || 0
  const totalPages = data?.totalPages || 0

  // ========================================
  // LAYOUT + TEMPLATE COMPOSITION
  // ========================================

  return (
    <PageLayout
      title="Organizations"
      description="Manage organizations and their users"
      icon={<Building2 className="h-6 w-6" />}
      onRefresh={handleRefresh}
      isRefreshing={isLoading}
      actions={
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Button onClick={() => setCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Organization
          </Button>
        </div>
      }
    >
      <OrganizationsTemplate
        organizations={organizations}
        total={total}
        page={page}
        limit={limit}
        totalPages={totalPages}
        isLoading={isLoading}
        error={error}
        createDialog={createDialog}
        onSetCreateDialog={setCreateDialog}
        onPageChange={setPage}
      />
    </PageLayout>
  )
}
